import { Injectable, inject, signal } from '@angular/core';
import { AccountInfo } from '@azure/msal-browser';
import { MsalService } from '@azure/msal-angular';
import { HttpClient } from '@angular/common/http';
import { Subject, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { LegalConsentService, UserLegalConsentStatusDto } from './legal-consent.service';

const DEV_ACCOUNT: AccountInfo = {
  homeAccountId: 'dev-home-account-id',
  environment: 'login.windows.net',
  tenantId: 'dev-tenant-id',
  username: 'dev@devuser.local',
  localAccountId: 'dev-local-account-id',
  name: 'Dev User',
  idTokenClaims: {}
};

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private static readonly SimRolesStorageKey = 'utb.dev.simRoles';

  private readonly msalService = inject(MsalService);
  private readonly httpClient = inject(HttpClient);
  private readonly legalConsentService = inject(LegalConsentService);
  private readonly _simLoggedIn = signal(environment.devSimulateAuth);
  private resolvedOrganizationId: number | null | undefined;
  private resolvedLegalConsent: UserLegalConsentStatusDto | null | undefined;

  /** Emits whenever the simulated auth state is toggled. */
  readonly simStateChanged$ = new Subject<void>();

  getActiveAccount(): AccountInfo | null {
    if (environment.devSimulateAuth) {
      return this._simLoggedIn() ? DEV_ACCOUNT : null;
    }

    const activeAccount = this.msalService.instance.getActiveAccount();
    if (activeAccount) {
      return activeAccount;
    }

    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
      this.msalService.instance.setActiveAccount(accounts[0]);
      return accounts[0];
    }

    return null;
  }

  getRoles(): string[] {
    if (environment.devSimulateAuth && this._simLoggedIn()) {
      return this.getSimulatedRoles();
    }

    const account = this.getActiveAccount();
    const claims = account?.idTokenClaims as Record<string, unknown> | undefined;
    const raw = claims?.['roles'];

    if (Array.isArray(raw)) {
      return raw.filter((v): v is string => typeof v === 'string');
    }

    if (typeof raw === 'string') {
      return [raw];
    }

    return [];
  }

  async getRolesAsync(): Promise<string[]> {
    const idTokenRoles = this.getRoles();
    if (idTokenRoles.length > 0) {
      return idTokenRoles;
    }

    if (environment.devSimulateAuth && this._simLoggedIn()) {
      return this.getSimulatedRoles();
    }

    const account = this.getActiveAccount();
    if (!account) {
      return [];
    }

    for (let attempt = 0; attempt < 3; attempt++) {
      const refreshedIdTokenRoles = this.getRoles();
      if (refreshedIdTokenRoles.length > 0) {
        return refreshedIdTokenRoles;
      }

      try {
        const result = await firstValueFrom(
          this.msalService.acquireTokenSilent({
            account,
            scopes: environment.apiConfig.scopes
          })
        );

        const accessTokenRoles = this.extractRolesFromJwt(result.accessToken);
        if (accessTokenRoles.length > 0) {
          return accessTokenRoles;
        }
      } catch {
        // Retry a few times after initial sign-in token propagation.
      }

      if (attempt < 2) {
        await this.delay(200);
      }
    }

    return [];
  }

  hasRole(role: string): boolean {
    return this.getRoles().includes(role);
  }

  async hasRoleAsync(role: string): Promise<boolean> {
    const roles = await this.getRolesAsync();
    return roles.includes(role);
  }

  getOrganizationId(): number | null {
    if (typeof this.resolvedOrganizationId !== 'undefined') {
      return this.resolvedOrganizationId;
    }

    const account = this.getActiveAccount();
    const claims = account?.idTokenClaims as Record<string, unknown> | undefined;
    const parsed = this.extractOrganizationIdFromClaims(claims);
    if (parsed !== null) {
      this.resolvedOrganizationId = parsed;
    }

    return parsed;
  }

  async getOrganizationIdAsync(): Promise<number | null> {
    if (typeof this.resolvedOrganizationId !== 'undefined') {
      return this.resolvedOrganizationId;
    }

    const fromIdToken = this.getOrganizationId();
    if (fromIdToken !== null) {
      this.resolvedOrganizationId = fromIdToken;
      return fromIdToken;
    }

    const account = this.getActiveAccount();
    if (!account) {
      return null;
    }

    try {
      const result = await firstValueFrom(
        this.msalService.acquireTokenSilent({
          account,
          scopes: environment.apiConfig.scopes
        })
      );

      const payload = this.extractJwtPayload(result.accessToken);
      const fromAccessToken = this.extractOrganizationIdFromClaims(payload);
      if (fromAccessToken !== null) {
        this.resolvedOrganizationId = fromAccessToken;
        return fromAccessToken;
      }
    } catch {
      // Ignore and fall back to API-based organization resolution.
    }

    const fromApi = await this.resolveOrganizationIdFromApiAsync();
    this.resolvedOrganizationId = fromApi;
    return fromApi;
  }

  async getLegalConsentStatusAsync(forceRefresh = false): Promise<UserLegalConsentStatusDto | null> {
    if (!forceRefresh && typeof this.resolvedLegalConsent !== 'undefined') {
      return this.resolvedLegalConsent;
    }

    const account = this.getActiveAccount();
    if (!account) {
      this.resolvedLegalConsent = null;
      return null;
    }

    try {
      const status = await firstValueFrom(this.legalConsentService.getMyStatus());
      this.resolvedLegalConsent = status;
      return status;
    } catch {
      this.resolvedLegalConsent = null;
      return null;
    }
  }

  async getPendingLegalRouteAsync(forceRefresh = false): Promise<string | null> {
    const status = await this.getLegalConsentStatusAsync(forceRefresh);
    if (!status) {
      return null;
    }

    if (!status.termsAndConditionsAccepted) {
      return '/terms-and-conditions';
    }

    if (!status.privacyPolicyAccepted) {
      return '/privacy-policy';
    }

    return null;
  }

  invalidateLegalConsentCache(): void {
    this.resolvedLegalConsent = undefined;
  }

  simLogin(): void {
    this._simLoggedIn.set(true);
    this.resolvedOrganizationId = undefined;
    this.resolvedLegalConsent = undefined;
    this.simStateChanged$.next();
  }

  simLogout(): void {
    this._simLoggedIn.set(false);
    this.resolvedOrganizationId = undefined;
    this.resolvedLegalConsent = undefined;
    this.simStateChanged$.next();
  }

  private async resolveOrganizationIdFromApiAsync(): Promise<number | null> {
    try {
      const organization = await firstValueFrom(this.httpClient.get<{ id?: number }>('/api/organizations/me'));
      return this.parseNumericClaim(organization?.id);
    } catch {
      return null;
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private extractRolesFromJwt(accessToken: string): string[] {
    const payload = this.extractJwtPayload(accessToken);
    const raw = payload?.['roles'];

    if (Array.isArray(raw)) {
      return raw.filter((v): v is string => typeof v === 'string');
    }

    if (typeof raw === 'string') {
      return [raw];
    }

    return [];
  }

  private extractJwtPayload(accessToken: string): Record<string, unknown> | null {
    const parts = accessToken.split('.');
    if (parts.length < 2) {
      return null;
    }

    try {
      return JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  private extractOrganizationIdFromClaims(claims?: Record<string, unknown> | null): number | null {
    if (!claims) {
      return null;
    }

    const claimKeys = [
      'organizationId',
      'organization_id',
      'orgId',
      'org_id',
      'extension_OrganizationId',
      'extension_organizationId',
      'extension_orgId'
    ];

    for (const key of claimKeys) {
      const parsed = this.parseNumericClaim(claims[key]);
      if (parsed !== null) {
        return parsed;
      }
    }

    for (const [key, value] of Object.entries(claims)) {
      if (/organizationid$/i.test(key) || /orgid$/i.test(key)) {
        const parsed = this.parseNumericClaim(value);
        if (parsed !== null) {
          return parsed;
        }
      }
    }

    return null;
  }

  private parseNumericClaim(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return Math.trunc(value);
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }

      const parsed = Number(trimmed);
      if (Number.isFinite(parsed) && parsed > 0) {
        return Math.trunc(parsed);
      }
    }

    return null;
  }

  private getSimulatedRoles(): string[] {
    const defaultRoles = ['SiteAdmin'];

    if (!environment.devSimulateAuth) {
      return defaultRoles;
    }

    try {
      const raw = globalThis.localStorage?.getItem(AuthService.SimRolesStorageKey)?.trim();
      if (!raw) {
        return defaultRoles;
      }

      const parsed = raw.split(',').map(v => v.trim()).filter(v => v.length > 0);
      return parsed.length > 0 ? parsed : defaultRoles;
    } catch {
      return defaultRoles;
    }
  }
}
